import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTeams } from '../../store/slices/teamSlice';
import { fetchTournamentDetails } from '../../store/slices/tournamentSlice';
import { fetchMatches, createMatch, updateMatch, deleteMatch } from '../../store/slices/matchSlice';
import { useParams, Link } from 'react-router-dom';
import {
    ChevronLeft, Calendar, MapPin, Trophy, Plus, Save, X, Edit, Trash2, Clock, Ticket
} from 'lucide-react';

const AdminTournamentFixture = () => {
    const { slug, id } = useParams();
    const dispatch = useDispatch();

    const { tournamentDetails } = useSelector(state => state.tournaments);
    const { teams } = useSelector(state => state.teams);
    const { matches, loading } = useSelector(state => state.matches);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);

    // Form state
    const [roundName, setRoundName] = useState('Jornada 1');
    const [teamA, setTeamA] = useState('');
    const [teamB, setTeamB] = useState('');
    const [date, setDate] = useState('');
    const [location, setLocation] = useState('');
    const [status, setStatus] = useState('Programado');
    const [scoreA, setScoreA] = useState(0);
    const [scoreB, setScoreB] = useState(0);
    const [isTicketed, setIsTicketed] = useState(false);
    const [ticketPrice, setTicketPrice] = useState(0);
    const [totalTickets, setTotalTickets] = useState(0);

    useEffect(() => {
        dispatch(fetchTournamentDetails({ slug, id }));
        dispatch(fetchTeams(id));
        dispatch(fetchMatches(id));
    }, [id, slug, dispatch]);

    const openCreateModal = () => {
        setEditingMatch(null);
        setRoundName('Jornada 1');
        setTeamA('');
        setTeamB('');
        setDate('');
        setLocation('');
        setStatus('Programado');
        setScoreA(0);
        setScoreB(0);
        setIsTicketed(false);
        setTicketPrice(0);
        setTotalTickets(0);
        setIsModalOpen(true);
    };

    const openEditModal = (match) => {
        setEditingMatch(match);
        setRoundName(match.roundName || '');
        setTeamA(match.teamA?._id || '');
        setTeamB(match.teamB?._id || '');
        setDate(match.date ? new Date(match.date).toISOString().slice(0, 16) : '');
        setLocation(match.location || '');
        setStatus(match.status || 'Programado');
        setScoreA(match.scoreA || 0);
        setScoreB(match.scoreB || 0);
        setIsTicketed(match.isTicketed || false);
        setTicketPrice(match.ticketPrice || 0);
        setTotalTickets(match.totalTickets || 0);
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            tournamentId: id,
            roundName,
            teamA: teamA || null,
            teamB: teamB || null,
            date: date || null,
            location,
            status,
            scoreA: Number(scoreA),
            scoreB: Number(scoreB),
            isTicketed,
            ticketPrice: Number(ticketPrice),
            totalTickets: Number(totalTickets)
        };

        try {
            if (editingMatch) {
                await dispatch(updateMatch({ slug, matchId: editingMatch._id, data })).unwrap();
            } else {
                await dispatch(createMatch({ slug, data })).unwrap();
            }
            setIsModalOpen(false);
        } catch (err) {
            alert(err.error || 'Error al guardar el partido');
        }
    };

    const handleDelete = (matchId) => {
        if (window.confirm('¿Seguro que querés eliminar este partido?')) {
            dispatch(deleteMatch({ slug, matchId }));
        }
    };

    // Agrupar partidos por Ronda
    const rounds = matches.reduce((acc, match) => {
        const round = match.roundName || 'Sin asignar';
        if (!acc[round]) acc[round] = [];
        acc[round].push(match);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-[#080808] text-white pb-20">
            <div className="max-w-6xl mx-auto px-6 pt-12">
                <Link
                    to={`/${slug}/admin/tournaments`}
                    className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 w-fit"
                >
                    <ChevronLeft size={20} />
                    <span className="font-black text-[10px] uppercase tracking-[2px]">Volver a Torneos</span>
                </Link>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                    <div>
                        <h1 className="text-4xl sm:text-5xl font-black mb-2">Fixture y Resultados</h1>
                        <p className="text-white/40 text-lg">{tournamentDetails?.name}</p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-black text-sm flex items-center gap-2 transition-all shadow-lg shadow-blue-500/20"
                    >
                        <Plus size={18} /> AGREGAR PARTIDO
                    </button>
                </div>

                {Object.keys(rounds).length === 0 ? (
                    <div className="bg-glass p-20 rounded-[40px] text-center border border-white/5 shadow-3xl">
                        <Calendar size={64} className="mx-auto text-white/10 mb-6" />
                        <p className="text-xl font-bold text-white/40 mb-4">Aún no hay partidos programados.</p>
                        <button onClick={openCreateModal} className="text-blue-400 font-bold hover:underline">Comenzá a armar el fixture</button>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {Object.entries(rounds).map(([roundName, roundMatches]) => (
                            <div key={roundName}>
                                <h3 className="text-2xl font-black uppercase text-blue-500 mb-6 flex items-center gap-3">
                                    <Trophy size={20} /> {roundName}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {roundMatches.map(match => (
                                        <div key={match._id} className="bg-glass border border-white/5 rounded-3xl p-6 relative group overflow-hidden">
                                            {/* Estado */}
                                            <div className={`absolute top-0 left-0 w-full h-1 ${match.status === 'Finalizado' ? 'bg-green-500' :
                                                    match.status === 'En Juego' ? 'bg-yellow-500' :
                                                        'bg-blue-500'
                                                }`} />

                                            <div className="flex justify-between items-start mb-4">
                                                <span className={`text-[10px] font-black uppercase tracking-[2px] px-2 py-1 rounded bg-white/5 ${match.status === 'Finalizado' ? 'text-green-400' :
                                                        match.status === 'En Juego' ? 'text-yellow-400 animate-pulse' :
                                                            'text-blue-400'
                                                    }`}>
                                                    {match.status}
                                                </span>
                                                {match.isTicketed && (
                                                    <span className="ml-2 text-[10px] font-black uppercase tracking-[2px] px-2 py-1 rounded bg-green-500/10 text-green-400 flex items-center gap-1">
                                                        <Ticket size={12} /> Entradas
                                                    </span>
                                                )}

                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => openEditModal(match)} className="p-2 bg-white/5 rounded-lg hover:bg-white/10 text-blue-400"><Edit size={14} /></button>
                                                    <button onClick={() => handleDelete(match._id)} className="p-2 bg-white/5 rounded-lg hover:bg-white/10 text-red-400"><Trash2 size={14} /></button>
                                                </div>
                                            </div>

                                            {/* Equipos y Score */}
                                            <div className="flex justify-between items-center bg-black/40 rounded-2xl p-4 mb-4 border border-white/5">
                                                <div className="text-center w-[40%]">
                                                    <p className="font-black text-sm truncate" title={match.teamA?.name || 'A Definir'}>{match.teamA?.name || 'A Definir'}</p>
                                                </div>
                                                <div className="w-[20%] text-center">
                                                    <span className="text-2xl font-black tracking-tighter">
                                                        {match.status === 'Programado' ? 'vs' : `${match.scoreA} - ${match.scoreB}`}
                                                    </span>
                                                </div>
                                                <div className="text-center w-[40%]">
                                                    <p className="font-black text-sm truncate" title={match.teamB?.name || 'A Definir'}>{match.teamB?.name || 'A Definir'}</p>
                                                </div>
                                            </div>

                                            {/* Info adicional */}
                                            <div className="flex flex-col gap-2 mt-4 text-xs font-medium text-white/40">
                                                {match.date && (
                                                    <div className="flex items-center gap-2">
                                                        <Clock size={12} className="text-white/20" />
                                                        {new Date(match.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                                    </div>
                                                )}
                                                {match.location && (
                                                    <div className="flex items-center gap-2">
                                                        <MapPin size={12} className="text-white/20" />
                                                        {match.location}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal Crear/Editar Partido */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#0f0f0f] border border-white/10 rounded-[32px] w-full max-w-lg p-8 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-black">{editingMatch ? 'Editar Partido' : 'Nuevo Partido'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white"><X size={24} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Jornada / Ronda</label>
                                <input
                                    type="text"
                                    required
                                    value={roundName}
                                    onChange={e => setRoundName(e.target.value)}
                                    placeholder="Ej: Fecha 1, Semifinal"
                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:border-blue-500 outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Equipo Local</label>
                                    <select value={teamA} onChange={e => setTeamA(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm outline-none">
                                        <option value="">A Definir</option>
                                        {teams.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Equipo Visitante</label>
                                    <select value={teamB} onChange={e => setTeamB(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm outline-none">
                                        <option value="">A Definir</option>
                                        {teams.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Fecha y Hora</label>
                                    <input
                                        type="datetime-local"
                                        value={date}
                                        onChange={e => setDate(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm outline-none"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Cancha / Lugar</label>
                                    <input
                                        type="text"
                                        value={location}
                                        onChange={e => setLocation(e.target.value)}
                                        placeholder="Ej: Cancha 1"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 border-t border-white/5 pt-5 mt-2">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Goles L.</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={scoreA}
                                        onChange={e => setScoreA(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-center font-black text-xl outline-none focus:bg-blue-500/10 focus:border-blue-500"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Goles V.</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={scoreB}
                                        onChange={e => setScoreB(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-center font-black text-xl outline-none focus:bg-blue-500/10 focus:border-blue-500"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Estado</label>
                                    <select value={status} onChange={e => setStatus(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm outline-none">
                                        <option value="Programado">Programado</option>
                                        <option value="En Juego">En Juego</option>
                                        <option value="Finalizado">Finalizado</option>
                                        <option value="Suspendido">Suspendido</option>
                                    </select>
                                </div>
                            </div>


                            {/* VENTA DE ENTRADAS */}
                            <div className="border-t border-white/5 pt-5 mt-2 space-y-4">
                                <div className="flex items-center justify-between bg-white/5 border border-white/10 p-4 rounded-xl">
                                    <div>
                                        <h4 className="text-sm font-black text-white">Vender Entradas</h4>
                                        <p className="text-[10px] text-white/40 mt-1">Habilitar cobro de entradas para este partido.</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isTicketed}
                                            onChange={(e) => setIsTicketed(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                                    </label>
                                </div>

                                {isTicketed && (
                                    <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Precio ($)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                required={isTicketed}
                                                value={ticketPrice}
                                                onChange={e => setTicketPrice(e.target.value)}
                                                className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Capacidad Total</label>
                                            <input
                                                type="number"
                                                min="1"
                                                required={isTicketed}
                                                value={totalTickets}
                                                onChange={e => setTotalTickets(e.target.value)}
                                                className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button type="submit" className="w-full py-4 mt-6 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2">
                                <Save size={18} /> GUARDAR PARTIDO
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTournamentFixture;
